import {
  badWords,
  maxLength,
  required,
  requiredSelection,
} from '@seek/validators-js';
import {
  Box,
  Checkbox,
  Dropdown,
  FieldLabel,
  FieldMessage,
  RadioGroup,
  RadioItem,
  Stack,
  Textarea,
} from 'braid-design-system';
import type { FormEventHandler } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import type {
  FreeTextQuestion,
  MultipleChoiceQuestion,
  SingleChoiceQuestion,
} from 'src/__generated__/graphql';
import {
  isFreeTextQuestion,
  isMultipleChoiceQuestion,
  isSingleChoiceQuestion,
  questionIdToFieldKey,
} from 'src/components/RoleRequirementsForm/utils';
import { useConfig } from 'src/context/config';
import { createFieldValidator } from 'src/forms/validator';
import { AlertMessage } from 'src/ui/AlertMessage/AlertMessage';
import { ScrollOnMount } from 'src/ui/ScrollOnMount/ScrollOnMount';

export const ROLE_REQUIREMENT_NAMESPACE = 'questionnaire';

type FreeTextQuestionWithAnswer = {
  lastAnswer?: string;
} & FreeTextQuestion;

export type Question =
  | SingleChoiceQuestion
  | MultipleChoiceQuestion
  | FreeTextQuestionWithAnswer;

interface Props {
  questions: Question[];
  onSubmit?: FormEventHandler<HTMLFormElement>;
  actions: React.ReactNode;
}

export function RoleRequirementsForm({
  questions,
  onSubmit = () => {},
  actions,
}: Props) {
  const { config } = useConfig();

  const { formState } = useFormContext();
  const validationErrors = Object.entries(
    formState.errors[ROLE_REQUIREMENT_NAMESPACE] || {},
  );
  const validationMessagesMap = validationErrors.reduce((map, [key, error]) => {
    const fieldName = questions.find(
      (q) => questionIdToFieldKey(q.id) === key,
    )?.text;
    if (!fieldName) return map;
    return { ...map, [fieldName]: error.message };
  }, {});

  return (
    <Stack space="xlarge">
      {validationErrors.length > 0 && (
        <ScrollOnMount key={formState.submitCount}>
          <AlertMessage message={validationMessagesMap} />
        </ScrollOnMount>
      )}
      <Box component="form" onSubmit={onSubmit}>
        <Stack space="xlarge">
          {questions.map((question) => {
            const questionId = `question-${question.id}` as const;
            const fieldKey = questionIdToFieldKey(question.id);
            const label = question.text;
            const name = `${ROLE_REQUIREMENT_NAMESPACE}.${fieldKey}`;

            if (isFreeTextQuestion(question)) {
              const defaultValue = question.lastAnswer! || '';
              return (
                <Controller
                  key={question.id}
                  name={name}
                  defaultValue={defaultValue}
                  rules={{
                    validate: createFieldValidator(
                      [required, maxLength, badWords],
                      { max: 1000 },
                      config.LANGUAGE,
                    ),
                  }}
                  render={({ field, fieldState }) => (
                    <Textarea
                      id={questionId}
                      label={label}
                      ref={field.ref}
                      name={field.name}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      value={field.value}
                      tone={fieldState.invalid ? 'critical' : 'neutral'}
                      message={fieldState.error?.message}
                    />
                  )}
                />
              );
            }

            if (isSingleChoiceQuestion(question)) {
              const defaultValue = question.lastAnswer?.id;
              if (question.options.length >= 6) {
                return (
                  <Controller
                    key={question.id}
                    name={name}
                    rules={{
                      validate: createFieldValidator(
                        [requiredSelection],
                        {},
                        config.LANGUAGE,
                      ),
                    }}
                    defaultValue={defaultValue}
                    render={({ field, fieldState }) => (
                      <Dropdown
                        id={questionId}
                        label={label}
                        ref={field.ref}
                        name={field.name}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        value={field.value}
                        tone={fieldState.invalid ? 'critical' : 'neutral'}
                        message={fieldState.error?.message}
                      >
                        {question.options.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.text}
                          </option>
                        ))}
                      </Dropdown>
                    )}
                  />
                );
              }
              return (
                <Controller
                  key={question.id}
                  name={name}
                  rules={{
                    validate: createFieldValidator(
                      [requiredSelection],
                      {},
                      config.LANGUAGE,
                    ),
                  }}
                  defaultValue={defaultValue}
                  render={({ field, fieldState }) => (
                    <RadioGroup
                      id={questionId}
                      label={label}
                      name={field.name}
                      value={field.value as string}
                      onChange={field.onChange}
                      tone={fieldState.invalid ? 'critical' : 'neutral'}
                      message={fieldState.error?.message}
                    >
                      {question.options.map((option) => (
                        <RadioItem
                          key={option.id}
                          data={{ testid: `${option.id}${question.id}` }}
                          label={option.text}
                          value={option.id}
                        />
                      ))}
                    </RadioGroup>
                  )}
                />
              );
            }

            if (isMultipleChoiceQuestion(question)) {
              const defaultValue = question.lastAnswers?.map((a) => a.id) || [];

              return (
                <Controller
                  key={question.id}
                  name={name}
                  rules={{
                    validate: createFieldValidator(
                      [requiredSelection],
                      {},
                      config.LANGUAGE,
                    ),
                  }}
                  defaultValue={defaultValue}
                  render={({ field, fieldState }) => (
                    <Stack space="medium">
                      <FieldLabel htmlFor={false} label={label} />
                      {question.options.map((option) => (
                        <Checkbox
                          key={option.id}
                          id={option.id}
                          data={{ testid: `${option.id}${question.id}` }}
                          label={option.text}
                          name={field.name}
                          checked={(field.value as string[]).includes(
                            option.id,
                          )}
                          onChange={(e) => {
                            if ((e.target as HTMLInputElement).checked) {
                              field.onChange([...field.value, option.id]);
                            } else {
                              field.onChange(
                                (field.value as string[]).filter(
                                  (id) => id !== option.id,
                                ),
                              );
                            }
                          }}
                          tone={fieldState.invalid ? 'critical' : 'neutral'}
                        />
                      ))}
                      {fieldState.error && (
                        <FieldMessage
                          id={`${question.id}-message`}
                          tone="critical"
                          message={fieldState.error.message}
                        />
                      )}
                    </Stack>
                  )}
                />
              );
            }

            return null;
          })}

          <>{actions}</>
        </Stack>
      </Box>
    </Stack>
  );
}
